import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Plus, UserPlus, Search, Stethoscope, CalendarDays, FileText, FileCheck, History as HistoryIcon, Mail, Phone, ClipboardList, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, formatError } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const emptyPatient = {
  name: "", email: "", phone: "", nif: "", dob: "", address: "",
  blood_type: "", allergies: "", notes: "",
  send_activation: false,
};

const emptyAppt = {
  patient_id: "", date: "", time: "10:00", reason: "", mode: "in_person",
  payment_status: "paid", notify_patient: false,
};

export default function Patients() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);
  const [openNew, setOpenNew] = useState(false);
  const [openAppt, setOpenAppt] = useState(null); // patient object when set
  const [form, setForm] = useState(emptyPatient);
  const [appt, setAppt] = useState(emptyAppt);
  const [doctorId, setDoctorId] = useState("");

  const load = () => api.get("/patients").then(({ data }) => setItems(data));

  useEffect(() => {
    load();
    // Prefer authenticated doctor id for quick appt creation
    if (user?.role === "doctor") {
      setDoctorId(user.id);
    } else {
      api.get("/doctors").then(({ data }) => {
        if (data.length) setDoctorId(data[0].id);
      }).catch(() => {});
    }
  }, [user]);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const s = q.trim().toLowerCase();
    return items.filter((p) =>
      (p.name || "").toLowerCase().includes(s) ||
      (p.email || "").toLowerCase().includes(s) ||
      (p.nif || "").toLowerCase().includes(s) ||
      (p.phone || "").toLowerCase().includes(s)
    );
  }, [items, q]);

  const submitPatient = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).filter(([, v]) => v !== "" && v !== null && v !== undefined)
      );
      const { data } = await api.post("/patients", payload);
      toast.success(`Paciente ${data.name} añadido`);
      setOpenNew(false);
      setForm(emptyPatient);
      load();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  const submitAppt = async (e) => {
    e.preventDefault();
    if (!doctorId) return toast.error("No se ha detectado el médico");
    setSaving(true);
    try {
      const payload = { ...appt, doctor_id: doctorId };
      const { data } = await api.post("/appointments", payload);
      toast.success(`Cita creada${data.payment_status === "paid" ? " · pagada" : ""}`);
      setOpenAppt(null);
      setAppt(emptyAppt);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  const openApptFor = (p) => {
    setAppt({ ...emptyAppt, patient_id: p.id });
    setOpenAppt(p);
  };

  const sendActivation = async (p) => {
    if (!p.email || String(p.email).endsWith("@nolinked.local")) {
      return toast.error("Este paciente no tiene email registrado");
    }
    try {
      await api.post(`/patients/${p.id}/send-activation`);
      toast.success(`Email de activación enviado a ${p.email}`);
    } catch (err) { toast.error(formatError(err)); }
  };

  const sendPreconsult = async (p) => {
    if (!p.email || String(p.email).endsWith("@nolinked.local")) {
      return toast.error("Este paciente no tiene email registrado");
    }
    try {
      await api.post("/interrogations/send-preconsult", { patient_id: p.id });
      toast.success("Interrogatorio pre-consulta enviado por email");
    } catch (err) { toast.error(formatError(err)); }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="patients-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <h1 className="font-display text-2xl sm:text-3xl flex items-center gap-3">
          <UserPlus className="w-7 h-7 sm:w-8 sm:h-8 text-primary" />
          Pacientes
        </h1>
        <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-initial min-w-0">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, email, DNI..." className="pl-9 w-full sm:w-72" data-testid="patient-search" />
          </div>
          <Dialog open={openNew} onOpenChange={setOpenNew}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary/90 shrink-0" data-testid="new-patient-btn">
                <Plus className="w-4 h-4 mr-2" /> <span className="hidden sm:inline">Añadir paciente</span><span className="sm:hidden">Añadir</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Alta rápida de paciente</DialogTitle>
                <DialogDescription>
                  El paciente queda registrado bajo su cuidado. No se le enviará contraseña; podrá activar su acceso mediante «Recuperar contraseña» cuando lo desee.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={submitPatient} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Nombre completo *</Label>
                    <Input required value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} data-testid="pat-name" />
                  </div>
                  <div>
                    <Label>DNI / NIE</Label>
                    <Input value={form.nif} onChange={(e) => setForm((p) => ({ ...p, nif: e.target.value }))} data-testid="pat-nif" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Email</Label>
                    <Input type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} data-testid="pat-email" />
                  </div>
                  <div>
                    <Label>Teléfono</Label>
                    <Input value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} data-testid="pat-phone" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Fecha de nacimiento</Label>
                    <Input type="date" value={form.dob} onChange={(e) => setForm((p) => ({ ...p, dob: e.target.value }))} data-testid="pat-dob" />
                  </div>
                  <div>
                    <Label>Grupo sanguíneo</Label>
                    <Select value={form.blood_type || "unset"} onValueChange={(v) => setForm((p) => ({ ...p, blood_type: v === "unset" ? "" : v }))}>
                      <SelectTrigger data-testid="pat-blood"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unset">—</SelectItem>
                        {["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"].map((b) => (
                          <SelectItem key={b} value={b}>{b}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Dirección</Label>
                  <Input value={form.address} onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))} data-testid="pat-address" />
                </div>
                <div>
                  <Label>Alergias conocidas</Label>
                  <Input value={form.allergies} onChange={(e) => setForm((p) => ({ ...p, allergies: e.target.value }))} placeholder="p.ej. Penicilina, AAS..." data-testid="pat-allergies" />
                </div>
                <div>
                  <Label>Notas / antecedentes</Label>
                  <Textarea rows={3} value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} data-testid="pat-notes" />
                </div>
                <div className="flex items-center gap-2">
                  <input id="send-activation" type="checkbox" checked={!!form.send_activation} onChange={(e) => setForm((p) => ({ ...p, send_activation: e.target.checked }))} disabled={!form.email} data-testid="pat-send-activation" />
                  <Label htmlFor="send-activation" className="cursor-pointer text-sm">
                    Enviar email de activación al paciente {!form.email && <span className="text-muted-foreground">(requiere email)</span>}
                  </Label>
                </div>
                <Button type="submit" disabled={saving || !form.name} className="w-full bg-primary hover:bg-primary/90" data-testid="pat-save-btn">
                  {saving ? "Guardando..." : "Registrar paciente"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="font-display text-lg">
            {filtered.length} paciente{filtered.length === 1 ? "" : "s"}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {filtered.length === 0 && (
            <div className="text-sm text-muted-foreground">Sin resultados. Añada su primer paciente.</div>
          )}
          {filtered.map((p) => (
            <div key={p.id} className="border border-border rounded-lg p-4 flex flex-wrap items-center gap-3 justify-between" data-testid={`patient-row-${p.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">{p.name}</span>
                  {p.nif && <span className="text-xs text-muted-foreground">· {p.nif}</span>}
                  {p.managed_by_doctor && <Badge variant="outline" className="text-[10px]">Gestionado</Badge>}
                  {p.medical_profile?.blood_type && <Badge className="text-[10px] bg-red-100 text-red-700">{p.medical_profile.blood_type}</Badge>}
                </div>
                <div className="text-xs text-muted-foreground flex gap-3 flex-wrap mt-1">
                  {p.email && !String(p.email).endsWith("@nolinked.local") && (<span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {p.email}</span>)}
                  {p.phone && (<span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {p.phone}</span>)}
                  {p.medical_profile?.allergies && (<span className="text-red-700">⚠ {p.medical_profile.allergies}</span>)}
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button variant="outline" size="sm" onClick={() => openApptFor(p)} data-testid={`quick-appt-${p.id}`}>
                  <CalendarDays className="w-4 h-4 mr-1" /> Cita
                </Button>
                <Button asChild variant="outline" size="sm" data-testid={`quick-inperson-${p.id}`}>
                  <Link to={`/app/consulta-presencial/${p.id}`}>
                    <Stethoscope className="w-4 h-4 mr-1" /> Consulta
                  </Link>
                </Button>
                <Button asChild variant="outline" size="sm" data-testid={`quick-phone-${p.id}`} disabled={!p.phone}>
                  <Link to={`/app/consulta-telefonica/${p.id}`}>
                    <Phone className="w-4 h-4 mr-1" /> Teléfono
                  </Link>
                </Button>
                <Button asChild variant="outline" size="sm" data-testid={`quick-interrogation-${p.id}`}>
                  <Link to="/app/interrogatorios" state={{ patientId: p.id }}>
                    <ClipboardList className="w-4 h-4 mr-1" /> Interrogatorio
                  </Link>
                </Button>
                <Button asChild variant="outline" size="sm" data-testid={`quick-report-${p.id}`}>
                  <Link to="/app/informes" state={{ patientId: p.id }}>
                    <FileCheck className="w-4 h-4 mr-1" /> Informe
                  </Link>
                </Button>
                <Button asChild variant="ghost" size="sm" data-testid={`quick-history-${p.id}`}>
                  <Link to="/app/historia-clinica" state={{ patientId: p.id }}>
                    <HistoryIcon className="w-4 h-4 mr-1" /> Historia
                  </Link>
                </Button>
                {p.email && !String(p.email).endsWith("@nolinked.local") && (
                  <>
                    <Button variant="ghost" size="sm" onClick={() => sendPreconsult(p)} data-testid={`send-preconsult-${p.id}`} title="Enviar interrogatorio pre-consulta">
                      <Mail className="w-4 h-4 mr-1" /> Pre-consulta
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => sendActivation(p)} data-testid={`send-activation-${p.id}`} title="Enviar email de activación de cuenta">
                      <KeyRound className="w-4 h-4 mr-1" /> Activar
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Dialog open={!!openAppt} onOpenChange={(o) => !o && setOpenAppt(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nueva cita para {openAppt?.name}</DialogTitle>
            <DialogDescription>
              Configure la cita. Como médico puede marcarla como pagada manualmente (presencial en consulta).
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitAppt} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Fecha *</Label>
                <Input required type="date" value={appt.date} onChange={(e) => setAppt((p) => ({ ...p, date: e.target.value }))} data-testid="appt-date" />
              </div>
              <div>
                <Label>Hora *</Label>
                <Input required type="time" value={appt.time} onChange={(e) => setAppt((p) => ({ ...p, time: e.target.value }))} data-testid="appt-time" />
              </div>
            </div>
            <div>
              <Label>Motivo *</Label>
              <Input required value={appt.reason} onChange={(e) => setAppt((p) => ({ ...p, reason: e.target.value }))} data-testid="appt-reason" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Modalidad</Label>
                <Select value={appt.mode} onValueChange={(v) => setAppt((p) => ({ ...p, mode: v }))}>
                  <SelectTrigger data-testid="appt-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in_person">Presencial</SelectItem>
                    <SelectItem value="video">Videoconsulta</SelectItem>
                    <SelectItem value="phone">Llamada telefónica</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Estado de pago</Label>
                <Select value={appt.payment_status} onValueChange={(v) => setAppt((p) => ({ ...p, payment_status: v }))}>
                  <SelectTrigger data-testid="appt-payment"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="paid">Pagada (manual)</SelectItem>
                    <SelectItem value="pending">Pendiente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input id="appt-notify" type="checkbox" checked={appt.notify_patient} onChange={(e) => setAppt((p) => ({ ...p, notify_patient: e.target.checked }))} data-testid="appt-notify" />
              <Label htmlFor="appt-notify" className="cursor-pointer text-sm">Enviar email de confirmación al paciente</Label>
            </div>
            <Button type="submit" disabled={saving || !appt.date || !appt.time || !appt.reason} className="w-full bg-primary hover:bg-primary/90" data-testid="appt-save-btn">
              {saving ? "Creando..." : "Crear cita"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
